import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import { TeamBG } from "../../constant";
import { teamMemberData } from "../../helper";
import axios from "axios";

const CarouselContainer = styled(Box)({
  width: "100%",
  height: "500px",
  position: "relative",
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  zIndex: 2,
  margin: "auto",
});

const CarouselTrack = styled(motion.div)({
  display: "flex",
  height: "100%",
  zIndex: 2,
});

const StyledCard = styled(Card)(({ theme }) => ({
  width: 300,
  height: 350,
  margin: "0 10px",
  borderRadius: "12px",
  overflow: "hidden",
  position: "relative",
  flexShrink: 0,
  boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
  background: "transparent",
}));

const CardBackground = styled(Box)({
  position: "absolute",
  width: "100%",
  height: "100%",
  zIndex: 1,
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

const CardContent = styled(Box)({
  position: "relative",
  zIndex: 2,
  width: "100%",
  height: "100%",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    position: "absolute",
  },
});

const ColorOverlay = styled(motion.div)({
  position: "absolute",
  bottom: 0,
  left: 0,
  width: "100%",
  background: 'linear-gradient(207.75deg, #1DA1F2 17.24%, #6843EC 82.76%)',
  zIndex: 2,
});

const TopContent = styled(motion.div)({
  position: "absolute",
  top: 20,
  left: 20,
  zIndex: 4,
  color: "white",
});

const DescriptionText = styled(motion.div)({
  position: "absolute",
  bottom: 20,
  left: 20,
  right: 20,
  zIndex: 4,
  color: "white",
  opacity: 0,
});

const AnimatedCard = motion.create(StyledCard);

const cardVariants = {
  hover: {
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.2,
    },
  },
};

const overlayVariants = {
  initial: { height: 0 },
  hover: { height: "100%" },
};

const contentVariants = {
  initial: { y: 250, opacity: 1 },
  hover: {
    opacity: 1,
    y: 10,
    transition: { delay: 0.2 },
  },
};

const descVariants = {
  initial: { opacity: 0, y: 20 },
  hover: {
    opacity: 1,
    y: -25,
    transition: { delay: 0.3 },
  },
};

const imageVariants = {
  hover: { opacity: 0, transition: { delay: 0 } },
};


export default function TeamCardsCarousel({teamMemberData}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const handleSlideChange = (direction) => {
    setCurrentIndex((prev) =>
      direction === "prev"
        ? prev === 0
          ? teamMemberData?.length - 1
          : prev - 1
        : prev === teamMemberData?.length - 1
          ? 0
          : prev + 1
    );
  };

  const getVisibleCards = () => {
    const cardsToShow = isMobile ? 1 : 4;
    let visibleCards = [];

    for (let i = 0; i < cardsToShow; i++) {
      const index = (currentIndex + i) % teamMemberData?.length;
      visibleCards.push(teamMemberData?.[index]);
    }

    return visibleCards;
  };


  return (
    <Box sx={{ pt: 4, position: "relative", zIndex: 2 }}>
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            gap: 3,
            width: "100%",
            justifyContent: "center",
            mb: 4,
          }}
        >
          <button
            onClick={() => handleSlideChange("prev")}
            className="circular-btn"
            style={{ cursor: "pointer" }}
          >
            &lt;
          </button>
          <button
            onClick={() => handleSlideChange("next")}
            className="circular-btn"
            style={{ cursor: "pointer" }}
          >
            &gt;
          </button>
        </Box>
      )}
      <CarouselContainer>
        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              gap: 3,
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "center", md: "flex-end" },
              mb: 4,
              padding: 4,
              zIndex: 10,
            }}
          >
            <button
              onClick={() => handleSlideChange("prev")}
              className="circular-btn"
              style={{ cursor: "pointer" }}
            >
              &lt;
            </button>
          </Box>
        )}

        <Box
          sx={{
            margin: "0 auto",
            overflow: "hidden",
            height: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CarouselTrack>
            {getVisibleCards()?.map((item, index) => (
              <AnimatedCard
                key={`${item?.id}-${index}`}
                variants={cardVariants}
                initial="initial"
                whileHover="hover"
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                {/* <CardBackground>
                  <img src={TeamBG} alt="background" />
                </CardBackground> */}

                <ColorOverlay
                  variants={overlayVariants}
                />

                <CardContent>
                  <motion.img
                    src={item?.contentJson?.image}
                    alt={item?.contentJson?.name}
                    style={{ filter: "brightness(0.7)" }}
                    variants={imageVariants}
                    transition={{ duration: 0.6 }}
                  />

                  <TopContent
                    variants={contentVariants}
                    transition={{ type: "spring" }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{ fontSize: "20px" }}
                    >
                      {item?.contentJson?.name}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "#61D1FA", fontSize: "16px" }}
                    >
                      {item?.contentJson?.designation}
                    </Typography>
                  </TopContent>

                  <DescriptionText
                    variants={descVariants}
                    transition={{ duration: 0.6 }}
                  >
                    <Typography variant="body2">
                      {item?.contentJson?.about ||
                        "Lorem ipsum dolor sit amet, consectetur adipiscing elit."}
                    </Typography>
                  </DescriptionText>
                </CardContent>
              </AnimatedCard>
            ))}
          </CarouselTrack>
        </Box>

        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              gap: 3,
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "center", md: "flex-end" },
              mb: 4,
              padding: 4,
              zIndex: 10,
            }}
          >
            <button
              onClick={() => handleSlideChange("next")}
              className="circular-btn"
              style={{ cursor: "pointer" }}
            >
              &gt;
            </button>
          </Box>
        )}
      </CarouselContainer>
    </Box>
  );
}